
export interface YouTubeTranscriptItem {
  text: string;
  start: number;
  duration: number;
}

export interface YouTubeChapter {
  title: string;
  start_time: number;
  timestamp: string;
}

export interface YouTubeVideoInfo {
  title: string;
  description: string;
  duration: string;
  channelTitle: string;
}

export interface YouTubeTranscriptResponse {
  transcript: YouTubeTranscriptItem[];
  chapters: YouTubeChapter[];
  has_chapters: boolean;
  full_text: string;
  language: string;
  total_segments: number;
  video_info: YouTubeVideoInfo;
}

import { config } from './config';

/**
 * Extract video ID from various YouTube URL formats
 */
export function extractVideoId(urlOrId: string): string | null {
  // If it's already just an ID (11 characters, alphanumeric + underscore/dash)
  if (/^[a-zA-Z0-9_-]{11}$/.test(urlOrId)) {
    return urlOrId;
  }

  // Regular expression patterns for different YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    /youtu\.be\/([^&\n?#]+)/
  ];

  for (const pattern of patterns) {
    const match = urlOrId.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Fetch video information from YouTube Data API
 */
export async function fetchVideoInfo(videoId: string): Promise<YouTubeVideoInfo | null> {
  try {
    // For now, use oEmbed API which doesn't require API key
    const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch video info');
    }

    const data = await response.json();
    
    return {
      title: data.title || 'Unknown Title',
      description: '', // oEmbed doesn't provide description
      duration: '', // oEmbed doesn't provide duration
      channelTitle: data.author_name || 'Unknown Channel'
    };
  } catch (error) {
    console.error('Error fetching video info:', error);
    return null;
  }
}

/**
 * Fetch transcript via backend API (server-side) to avoid CORS restrictions
 */
const transcriptCache: Map<string, { data: YouTubeTranscriptItem[]; expiresAt: number }> = new Map();
const transcriptFailureCooldown: Map<string, number> = new Map();

export async function fetchTranscript(videoId: string, language: string = 'en'): Promise<YouTubeTranscriptItem[]> {
  const cacheKey = `${videoId}|${language}`;
  const now = Date.now();

  const failUntil = transcriptFailureCooldown.get(cacheKey) || 0;
  if (now < failUntil) {
    throw new Error('Transcript temporarily unavailable');
  }

  const cached = transcriptCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.data;
  }

  try {
    // 🔄 BACKEND: Use our backend API for transcript fetching (avoids CORS issues)
    console.log(`🔍 Fetching transcript for video ${videoId} via backend API`);
    
    const backendUrl = `${config.BACKEND_API_URL}/api/youtube/transcript/${videoId}?lang=${language}`;
    
    const response = await fetch(backendUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        console.log(`📝 No transcript found for ${videoId}, trying without language parameter`);
        
        // Try without language parameter as fallback
        const fallbackUrl = `${config.BACKEND_API_URL}/api/youtube/transcript/${videoId}`;
        const fallbackResponse = await fetch(fallbackUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });
        
        if (!fallbackResponse.ok) {
          throw new Error(`Backend transcript API failed: ${fallbackResponse.status} ${fallbackResponse.statusText}`);
        }
        
        const fallbackData = await fallbackResponse.json();
        if (fallbackData.transcript && fallbackData.transcript.length > 0) {
          const transcript: YouTubeTranscriptItem[] = fallbackData.transcript;
          transcriptCache.set(cacheKey, { data: transcript, expiresAt: now + 10 * 60_000 });
          console.log(`✅ Successfully fetched ${transcript.length} transcript items via backend (fallback)`);
          return transcript;
        }
      } else {
        throw new Error(`Backend transcript API failed: ${response.status} ${response.statusText}`);
      }
    } else {
      const data = await response.json();
      if (data.transcript && data.transcript.length > 0) {
        const transcript: YouTubeTranscriptItem[] = data.transcript;
        transcriptCache.set(cacheKey, { data: transcript, expiresAt: now + 10 * 60_000 });
        console.log(`✅ Successfully fetched ${transcript.length} transcript items via backend`);
        return transcript;
      }
    }
    
    // If we get here, no transcript was available
    console.log(`❌ No transcript available for video ${videoId} from backend`);
    throw new Error('No transcript available from backend');
    
  } catch (error) {
    console.warn(`⚠️ Backend transcript fetch failed for video ${videoId}:`, error);
    
    // Set cooldown for failed attempts
    transcriptFailureCooldown.set(cacheKey, now + 60_000);
    
    // Return empty array instead of throwing to gracefully handle missing transcripts
    return [];
  }
}

// Now using backend API for transcript fetching to avoid CORS restrictions

/**
 * Parse chapter timestamps from video description
 */
export function parseChaptersFromDescription(description: string): YouTubeChapter[] {
  const chapters: YouTubeChapter[] = [];
  
  // Regular expression to match timestamp patterns like "0:00 Chapter Title" or "1:23:45 Another Chapter"
  const timestampPattern = /^(\d{1,2}:)?(\d{1,2}):(\d{2})\s+(.+)$/gm;
  
  let match;
  while ((match = timestampPattern.exec(description)) !== null) {
    const hours = match[1] ? parseInt(match[1].slice(0, -1)) : 0;
    const minutes = parseInt(match[2]);
    const seconds = parseInt(match[3]);
    const title = match[4].trim();
    
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    const timestamp = hours > 0 
      ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      : `${minutes}:${seconds.toString().padStart(2, '0')}`;
    
    chapters.push({
      title: title,
      start_time: totalSeconds,
      timestamp: timestamp
    });
  }
  
  // Sort by start time
  chapters.sort((a, b) => a.start_time - b.start_time);
  
  return chapters;
}

/**
 * Fetch chapters using YouTube Data API (requires API key)
 */
export async function fetchChaptersFromAPI(videoId: string, apiKey?: string): Promise<YouTubeChapter[]> {
  if (!apiKey) {
    return []; // Return empty array if no API key provided
  }

  try {
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&part=snippet&key=${apiKey}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch video data');
    }
    
    const data = await response.json();
    
    if (data.items && data.items.length > 0) {
      const video = data.items[0];
      const description = video.snippet.description || '';
      
      // Parse chapters from description
      return parseChaptersFromDescription(description);
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching chapters from API:', error);
    return [];
  }
}

/**
 * Create transcript sections as 30-second chunks for better readability
 */
export function createTranscriptSections(transcript: YouTubeTranscriptItem[]): YouTubeChapter[] {
  if (transcript.length === 0) return [];

  const sections: YouTubeChapter[] = [];
  const CHUNK_DURATION = 30; // 30 seconds per chunk
  
  let currentChunkStart = 0;
  let currentChunkItems: YouTubeTranscriptItem[] = [];
  
  for (let i = 0; i < transcript.length; i++) {
    const item = transcript[i];
    
    // If this is the first item or we're still within the 30-second window
    if (currentChunkItems.length === 0) {
      currentChunkStart = item.start;
      currentChunkItems.push(item);
    } else if (item.start - currentChunkStart < CHUNK_DURATION) {
      currentChunkItems.push(item);
    } else {
      // We've exceeded 30 seconds, create a section from current chunk
      if (currentChunkItems.length > 0) {
        const sectionStartTime = currentChunkItems[0].start;
        const startMinutes = Math.floor(sectionStartTime / 60);
        const startSeconds = Math.floor(sectionStartTime % 60);
        
        // Create a meaningful title from the first few words (limited to ~25 chars)
        const combinedText = currentChunkItems
          .map(item => item.text)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();
        
        const words = combinedText.split(' ').slice(0, 3).join(' ');
        let title = words.length > 25 ? words.substring(0, 22) + '...' : words;
        
        // Fallback if no meaningful text
        if (!title || title.length < 3) {
          title = `${startMinutes}:${startSeconds.toString().padStart(2, '0')}`;
        }
        
        sections.push({
          title: title,
          start_time: sectionStartTime,
          timestamp: `${startMinutes}:${startSeconds.toString().padStart(2, '0')}`
        });
      }
      
      // Start new chunk with current item
      currentChunkStart = item.start;
      currentChunkItems = [item];
    }
  }
  
  // Don't forget the last chunk
  if (currentChunkItems.length > 0) {
    const sectionStartTime = currentChunkItems[0].start;
    const startMinutes = Math.floor(sectionStartTime / 60);
    const startSeconds = Math.floor(sectionStartTime % 60);
    
    const combinedText = currentChunkItems
      .map(item => item.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    const words = combinedText.split(' ').slice(0, 3).join(' ');
    let title = words.length > 25 ? words.substring(0, 22) + '...' : words;
    
    if (!title || title.length < 3) {
      title = `${startMinutes}:${startSeconds.toString().padStart(2, '0')}`;
    }
    
    sections.push({
      title: title,
      start_time: sectionStartTime,
      timestamp: `${startMinutes}:${startSeconds.toString().padStart(2, '0')}`
    });
  }
  
  return sections;
}

/**
 * Process transcript items into 30-second readable chunks
 */
export function createTranscriptChunks(transcript: YouTubeTranscriptItem[]): YouTubeTranscriptItem[] {
  if (transcript.length === 0) return [];

  const chunks: YouTubeTranscriptItem[] = [];
  const CHUNK_DURATION = 30; // 30 seconds per chunk
  
  let currentChunkStart = 0;
  let currentChunkItems: YouTubeTranscriptItem[] = [];
  
  for (let i = 0; i < transcript.length; i++) {
    const item = transcript[i];
    
    // If this is the first item or we're still within the 30-second window
    if (currentChunkItems.length === 0) {
      currentChunkStart = item.start;
      currentChunkItems.push(item);
    } else if (item.start - currentChunkStart < CHUNK_DURATION) {
      currentChunkItems.push(item);
    } else {
      // We've exceeded 30 seconds, create a chunk from current items
      if (currentChunkItems.length > 0) {
        const chunkStartTime = currentChunkItems[0].start;
        const chunkEndTime = currentChunkItems[currentChunkItems.length - 1].start + 
                            currentChunkItems[currentChunkItems.length - 1].duration;
        const chunkDuration = chunkEndTime - chunkStartTime;
        
        // Combine all text from the chunk
        const combinedText = currentChunkItems
          .map(item => item.text)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();
        
        chunks.push({
          text: combinedText,
          start: chunkStartTime,
          duration: chunkDuration
        });
      }
      
      // Start new chunk with current item
      currentChunkStart = item.start;
      currentChunkItems = [item];
    }
  }
  
  // Don't forget the last chunk
  if (currentChunkItems.length > 0) {
    const chunkStartTime = currentChunkItems[0].start;
    const chunkEndTime = currentChunkItems[currentChunkItems.length - 1].start + 
                        currentChunkItems[currentChunkItems.length - 1].duration;
    const chunkDuration = chunkEndTime - chunkStartTime;
    
    const combinedText = currentChunkItems
      .map(item => item.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    chunks.push({
      text: combinedText,
      start: chunkStartTime,
      duration: chunkDuration
    });
  }
  
  return chunks;
}

/**
 * Main function to fetch all YouTube data (transcript + chapters)
 */
export async function fetchYouTubeData(videoId: string, apiKey?: string): Promise<YouTubeTranscriptResponse> {
  try {
    // Fetch video info and transcript in parallel
    const [videoInfo, rawTranscript] = await Promise.all([
      fetchVideoInfo(videoId),
      fetchTranscript(videoId).catch(() => [])
    ]);
    
    // Process transcript into 30-second chunks for better readability
    const transcript = createTranscriptChunks(rawTranscript);
    
    // Try to get chapters from API if available
    let chapters: YouTubeChapter[] = [];
    if (apiKey) {
      chapters = await fetchChaptersFromAPI(videoId, apiKey);
    }
    
    // If no chapters from API, create 30-second sections from chunked transcript
    if (chapters.length === 0) {
      chapters = createTranscriptSections(transcript);
    }
    
    const fullText = transcript.map(item => item.text).join(' ');
    
    console.log(`📝 Processed transcript into ${transcript.length} chunks (30s each) from ${rawTranscript.length} raw segments`);
    
    return {
      transcript,
      chapters,
      has_chapters: chapters.length > 0,
      full_text: fullText,
      language: 'en', // We default to English for now
      total_segments: transcript.length,
      video_info: videoInfo || {
        title: 'Unknown Title',
        description: '',
        duration: '',
        channelTitle: 'Unknown Channel'
      }
    };
  } catch (error) {
    console.error('Error fetching YouTube data:', error);
    throw error;
  }
}